import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Copy, Mail, QrCode, RefreshCw, Ban, Link2, Loader2, Eye } from "lucide-react";
import { handoverApi } from "./handoverApi";
import { fmtDateTime } from "./imageUtils";

const linkUrl = (token) => `${window.location.origin}/handover/${token}`;

const LinkActions = ({ link, client, onEmail, onRegenerate, onRevoke, busy }) => {
  const url = linkUrl(link.token);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { toast.error("Could not copy — long-press the QR instead"); }
  };
  const saveQr = () => {
    const a = document.createElement("a");
    a.download = "client-handover-qr.png";
    a.href = document.getElementById("handover-qr").toDataURL("image/png");
    a.click();
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-col items-center gap-2 rounded-xl bg-white border border-slate-200 p-4">
        <QRCodeCanvas id="handover-qr" value={url} size={196} level="M" includeMargin data-testid="handover-qr" />
        <p className="text-xs text-slate-500 text-center">Client scans this with their phone camera</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" onClick={copy} data-testid="handover-copy-link"><Copy className="w-4 h-4 mr-1.5" />Copy Link</Button>
        <Button variant="outline" onClick={saveQr} data-testid="handover-save-qr"><QrCode className="w-4 h-4 mr-1.5" />Save QR</Button>
      </div>
      <Button onClick={onEmail} disabled={busy || !client?.email} className="w-full btn-construction" data-testid="handover-email-link">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-1.5" />Email to {client?.email || "client"}</>}
      </Button>
      <div className="text-xs text-slate-500 space-y-0.5" data-testid="handover-link-stats">
        <p className="flex items-center gap-1"><Eye className="w-3 h-3" />Opened {link.view_count || 0} time{link.view_count === 1 ? "" : "s"}{link.last_viewed_at ? ` · last ${fmtDateTime(link.last_viewed_at)}` : ""}</p>
        {link.last_emailed_at && <p>Last emailed {fmtDateTime(link.last_emailed_at)} to {link.last_emailed_to}</p>}
        {link.client_type === "developer" && <p>This Developer link covers all of their dwellings on this site.</p>}
      </div>
      <div className="flex justify-between border-t border-slate-100 pt-3">
        <Button size="sm" variant="ghost" onClick={onRegenerate} disabled={busy} data-testid="handover-regenerate-link"><RefreshCw className="w-3.5 h-3.5 mr-1" />New link</Button>
        <Button size="sm" variant="ghost" onClick={onRevoke} disabled={busy} className="text-red-600" data-testid="handover-revoke-link"><Ban className="w-3.5 h-3.5 mr-1" />Revoke</Button>
      </div>
    </div>
  );
};

export const ShareHandoverLinkDialog = ({ dwelling, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const closeRef = React.useRef(onOpenChange);
  closeRef.current = onOpenChange;

  const load = useCallback(() => {
    if (!dwelling) return;
    handoverApi(`/dwellings/${dwelling.id}/link`).then(setData).catch((e) => { toast.error(e.message); closeRef.current(false); });
  }, [dwelling]);
  useEffect(() => { setData(null); load(); }, [load]);

  const run = async (fn, msg) => {
    setBusy(true);
    try { const r = await fn(); if (msg) toast.success(typeof msg === "function" ? msg(r) : msg); load(); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const link = data?.link;

  return (
    <Dialog open={!!dwelling} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm max-h-[92vh] overflow-y-auto" data-testid="share-handover-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2"><Link2 className="w-5 h-5 text-amber-600" />Client Handover Link</DialogTitle>
          <DialogDescription>
            {dwelling?.dwelling_number} · {data?.client?.company_name || data?.client?.name || "…"} ({data?.client?.client_type === "developer" ? "Developer" : "Purchaser"})
          </DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-amber-600" /></div>
        ) : link ? (
          <LinkActions link={link} client={data.client} busy={busy}
            onEmail={() => run(() => handoverApi(`/links/${link.id}/email`, { method: "POST" }), (r) => r.message)}
            onRegenerate={() => window.confirm("Create a new link? The current link will stop working.") &&
              run(() => handoverApi(`/links/${link.id}/regenerate`, { method: "POST" }), "New link created — share it with the client")}
            onRevoke={() => window.confirm("Revoke this link? The client will no longer be able to open it.") &&
              run(() => handoverApi(`/links/${link.id}`, { method: "DELETE" }), "Link revoked")} />
        ) : (
          <div className="space-y-3 text-center py-2" data-testid="handover-no-link">
            <p className="text-sm text-slate-600">No link yet. The client gets a private, read-only page showing their walkthrough items and progress — no login needed.</p>
            <Button onClick={() => run(() => handoverApi(`/dwellings/${dwelling.id}/link`, { method: "POST" }), "Link created")} disabled={busy}
              className="w-full btn-construction" data-testid="handover-create-link">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Handover Link"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
